import React, { useCallback, useEffect, useState } from "react";
import { EntityListAndEditPage } from "../../shared/components/page/EntityListAndEditPage";
import { useTranslation } from "react-i18next";
import { userCrudApi } from "./UsersApi";
import { defaultUser } from "./User";
import { UserForm } from "./UserForm";
import { MUIDataTableColumn } from "mui-datatables";
import { useApplicationFormatter, useVisibleApplications } from "../applications/ApplicationProvider";
import { useAccount } from "./AccountProvider";
import { Grid, useMediaQuery, useTheme } from "@mui/material";
import { Company } from "../companies/Company";
import { getAllCompanies } from "../companies/CompaniesApi";
import { useAxios } from "../../shared/technical/rest/AxiosProvider";
import { TableOptions } from "../../shared/components/tables/PaginatedContentPageTable";
import { usersTableColumns } from "./UsersTableColumns";
import { UsersPageTitle } from "./UsersPageTitle";
import { UsersMoreMenu } from "./UsersMoreMenu";
import { RoleAdjustmentsNotice } from "./RoleAdjustmentsNotice";
import { OutdatedPermissionsAlert } from "../../shared/components/page/OutdatedPermissionsAlert";
import {
  filtersToQueryString,
  UsersListFilters,
  UsersListFiltersBar,
} from "./UsersListFilters";

export function UsersPageContent() {
  const { t } = useTranslation();
  const axios = useAxios();
  const theme = useTheme();
  const smallScreens = useMediaQuery(theme.breakpoints.down('sm'));
  const account = useAccount();
  const applicationFormatter = useApplicationFormatter();
  const filteredApplications = useVisibleApplications();
  const [ companies, setCompanies ] = useState<Company[]>();
  const [ columns, setColumns ] = useState<MUIDataTableColumn[]>();
  const [ entityQueryParams, setEntityQueryParams ] = useState<string>();
  const [ filterParams, setFilterParams ] = useState<string>('');
  const [ userCount, setUserCount ] = useState<number>(0);

  useEffect(() => {
    getAllCompanies(axios).then(setCompanies);
  }, [ axios ]);

  useEffect(() => {
    setColumns(usersTableColumns(t, smallScreens, applicationFormatter, account));
  }, [ t, account, smallScreens, applicationFormatter ])

  const handleFiltersChange = useCallback((filters: UsersListFilters) => {
    setFilterParams(filtersToQueryString(filters));
  }, []);

  const tableOptions: TableOptions = {
    search: false,
    filter: false,
  }

  if (!columns || !companies) {
    return <></>
  }

  return (
      <>
        <EntityListAndEditPage crudApi={userCrudApi}
                               columns={columns}
                               defaultEntity={defaultUser}
                               i18nPrefix={'User'}
                               isEditable={true}
                               saveNotice={user => user.roleAdjustments?.length
                                 ? <RoleAdjustmentsNotice roleAdjustments={user.roleAdjustments} />
                                 : undefined}
                               title={<UsersPageTitle userCount={userCount} />}
                               additionalHeaderItem={
                                 <>
                                   <Grid item={true} sx={ {marginLeft: "auto"}}>
                                     <UsersMoreMenu userCount={userCount} entityQueryParams={entityQueryParams} />
                                   </Grid>
                                 </>}
                               aboveTable={
                                 <>
                                 <OutdatedPermissionsAlert />
                                 <UsersListFiltersBar
                                   applications={filteredApplications}
                                   companies={companies}
                                   account={account}
                                   applicationFormatter={applicationFormatter}
                                   onFiltersChange={handleFiltersChange}
                                 />
                                 </>
                               }
                               additionalQueryParams={filterParams}
                               editForm={(entity, handleSave, handleDelete, handleClose, handleRefresh) =>
                                   <UserForm user={entity} handleSave={handleSave} handleDelete={handleDelete} handleClose={handleClose}
                                             handleRefresh={handleRefresh} />}
                               tableOptions={tableOptions}
                               onEntityQueryParamsChange={setEntityQueryParams}
                               onTotalCountChange={setUserCount}
        />
      </>
  )
}
